/**
 * Rax App Shell Component
 *
 * Main application shell that handles:
 * - Auth state
 * - Onboarding flow
 * - Main dashboard routing
 */

import { LitElement, html, css } from "lit";
import { customElement, state, query } from "lit/decorators.js";
import type { PixelOffice } from "../../pixel-office/pixel-office.js";
import type { ResourceType } from "./agent-onboarding.js";
import type { IntegrationSetupDraft } from "./integration-setup-popup.js";
import type { StatusBarData } from "./status-bar.js";
import {
  raxApi,
  type UserInfo,
  type OnboardingStatus,
  type Agent,
  type SecretInfo,
} from "../../api/rax-api.js";
// Import child components
import "./onboarding.js";
import "./band-dashboard.js";
import "./create-agent-modal.js";
import "./status-bar.js";
import "./railway-dashboard.js";
import "./spend-tracker.js";
import "./audit-trail.js";
import "./model-picker.js";
import "./connections-manager.js";
import "./learning-ledger.js";
import "./autonomy-settings.js";
import "./agent-onboarding.js";
import "./integration-setup-popup.js";
import "./data-export.js";
import "./resizable-divider.js";
import "./activity-panel.js";
import "../views/agent-chat.js";
import "../../pixel-office/pixel-office.js";
import { detectTaskType, detectTaskFromTool } from "../../pixel-office/constants.js";
import { createDelegationPlan, type DelegationPlan } from "../utils/orchestrator.js";
import { icons } from "../icons.js";
import { raxTheme } from "../styles/rax-theme.js";

type AppView = "loading" | "onboarding" | "provisioning" | "dashboard" | "chat" | "settings";

// Map agent onboarding resource types to integration setup config IDs
const RESOURCE_TO_INTEGRATION: Partial<Record<ResourceType, string>> = {
  computer_kernel: "kernel",
  phone_sendblue: "imessage",
  email_resend: "resend",
  skills_github: "github",
  slack: "slack",
  discord: "discord",
  notes_notion: "notion",
  calendar_google: "google_calendar",
};

@customElement("rax-app")
export class RaxApp extends LitElement {
  static override styles = [
    raxTheme,
    css`
      :host {
        display: block;
        background: var(--bg-primary);
        color: var(--text-primary);
        min-height: 100vh;
        height: 100vh;
        --app-header-height: 69px;
        overflow: hidden; /* Prevent document-level scrolling */
      }
      
      /* Loading State */
      .loading {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        min-height: 100vh;
        gap: var(--space-4);
      }
      
      .loading-logo {
        font-size: 64px;
        animation: pulse 2s ease-in-out infinite;
      }
      
      @keyframes pulse {
        0%,
        100% {
          opacity: 1;
          transform: scale(1);
        }
        50% {
          opacity: 0.7;
          transform: scale(1.05);
        }
      }
      
      .loading-text {
        color: var(--text-secondary);
        font-size: var(--font-sm);
      }
      
      /* Error State */
      .error {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        min-height: 100vh;
        gap: var(--space-4);
        padding: var(--space-6);
        text-align: center;
      }
      
      .error-icon {
        font-size: 48px;
      }
      
      .error-title {
        font-size: var(--font-xl);
        font-weight: 600;
        color: var(--error);
      }
      
      .error-message {
        color: var(--text-secondary);
        max-width: 400px;
      }
      
      .retry-btn {
        margin-top: var(--space-4);
        padding: var(--space-3) var(--space-5);
        background: var(--rax-yellow);
        color: var(--rax-black);
        border: none;
        border-radius: var(--radius-md);
        font-weight: 600;
        cursor: pointer;
        transition: all var(--transition-fast);
      }
      
      .retry-btn:hover {
        background: var(--rax-gold);
        box-shadow: var(--shadow-glow);
      }
      
      /* App Layout */
      .app-layout {
        display: flex;
        flex-direction: column;
        min-height: 100vh;
        height: 100vh;
        overflow: hidden;
      }
      
      .app-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        min-height: var(--app-header-height);
        padding: var(--space-4) var(--space-6);
        background: var(--bg-secondary);
        border-bottom: 1px solid var(--border-color);
      }
      
      .logo {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        font-size: var(--font-xl);
        font-weight: 700;
        color: var(--rax-yellow);
        text-shadow: 0 0 20px var(--accent-glow);
      }
      
      .logo-icon {
        font-size: 28px;
      }
      
      .header-actions {
        display: flex;
        align-items: center;
        gap: var(--space-4);
      }
      
      .sidebar-toggle {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
        background: var(--bg-elevated);
        color: var(--text-secondary);
        cursor: pointer;
        transition: all var(--transition-fast);
      }
      
      .sidebar-toggle svg {
        width: 14px;
        height: 14px;
        stroke: currentColor;
        fill: none;
        stroke-width: 1.8px;
        stroke-linecap: round;
        stroke-linejoin: round;
      }
      
      .sidebar-toggle:hover {
        color: var(--rax-yellow);
        border-color: var(--rax-yellow);
      }
      
      .user-menu {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        padding: var(--space-2) var(--space-3);
        background: var(--bg-elevated);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
        cursor: pointer;
        transition: all var(--transition-fast);
      }
      
      .user-menu:hover {
        border-color: var(--rax-yellow);
      }
      
      .user-menu-container {
        position: relative;
      }
      
      .user-dropdown {
        position: absolute;
        top: 100%;
        right: 0;
        margin-top: var(--space-2);
        min-width: 200px;
        background: var(--bg-elevated);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
        box-shadow: var(--shadow-lg);
        z-index: 100;
        overflow: hidden;
      }
      
      .dropdown-item {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        width: 100%;
        padding: var(--space-3) var(--space-4);
        background: none;
        border: none;
        color: var(--text-primary);
        font-size: var(--font-sm);
        text-align: left;
        cursor: pointer;
        transition: background var(--transition-fast);
      }
      
      .dropdown-item:hover {
        background: var(--bg-secondary);
      }
      
      .dropdown-item.danger {
        color: var(--error);
      }
      
      .dropdown-divider {
        height: 1px;
        background: var(--border-color);
        margin: var(--space-1) 0;
      }
      
      /* Dashboard Summary */
      .dashboard-summary {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
        gap: var(--space-4);
        padding: var(--space-6);
        background: var(--bg-secondary);
        border-bottom: 1px solid var(--border-color);
      }
      
      .summary-card {
        padding: var(--space-4);
        background: var(--bg-elevated);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
      }
      
      .summary-label {
        font-size: var(--font-xs);
        color: var(--text-tertiary);
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }
      
      .summary-value {
        font-size: var(--font-2xl);
        font-weight: 700;
        color: var(--rax-yellow);
        margin-top: var(--space-1);
      }
      
      .summary-value.green {
        color: var(--success);
      }
      
      .summary-subtext {
        font-size: var(--font-xs);
        color: var(--text-secondary);
        margin-top: var(--space-1);
      }
      
      .user-avatar {
        width: 28px;
        height: 28px;
        border-radius: 50%;
        background: var(--rax-yellow);
        color: var(--rax-black);
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 600;
        font-size: var(--font-xs);
      }
      
      .user-name {
        font-size: var(--font-sm);
        color: var(--text-primary);
      }
      
      .app-main {
        flex: 1;
        overflow: auto;
        min-height: 0;
      }
      
      .dashboard-shell {
        flex: 1;
        min-height: 0;
        display: grid;
        grid-template-columns: 250px minmax(0, 1fr);
      }
      
      .dashboard-shell.nav-collapsed {
        grid-template-columns: 80px minmax(0, 1fr);
      }
      
      .dashboard-main {
        min-width: 0;
        min-height: 0;
        display: flex;
        flex-direction: column;
      }
      
      .sidebar-nav {
        border-right: 1px solid var(--border-color);
        background: var(--bg-secondary);
        padding: var(--space-4);
        display: flex;
        flex-direction: column;
        gap: var(--space-2);
      }
      
      .sidebar-nav-title {
        font-size: 10px;
        color: var(--text-tertiary);
        text-transform: uppercase;
        letter-spacing: 0.08em;
        margin: var(--space-4) 0 var(--space-2) var(--space-2);
        font-weight: 500;
      }
      
      .sidebar-nav-title:first-child {
        margin-top: 0;
      }
      
      .nav-item {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        width: 100%;
        border: 1px solid transparent;
        border-radius: var(--radius-md);
        padding: 10px 11px;
        color: var(--text-secondary);
        background: transparent;
        font-size: 12px;
        font-weight: 500;
        text-align: left;
        cursor: pointer;
        transition: all var(--transition-fast);
      }
      
      .nav-item:hover {
        color: var(--text-primary);
        border-color: var(--border-color);
        background: var(--bg-elevated);
      }
      
      .nav-item.active {
        color: var(--rax-yellow);
        border-color: rgba(255, 208, 0, 0.4);
        background: rgba(255, 208, 0, 0.12);
      }
      
      .nav-item-icon {
        width: 16px;
        height: 16px;
        text-align: center;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex: 0 0 auto;
        color: currentColor;
      }
      
      .nav-item-icon svg {
        width: 16px;
        height: 16px;
        stroke: currentColor;
        fill: none;
        stroke-width: 1.8px;
        stroke-linecap: round;
        stroke-linejoin: round;
      }
      
      .nav-item-label {
        flex: 1;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      
      .dashboard-shell.nav-collapsed .nav-item {
        justify-content: center;
        padding: var(--space-3) var(--space-2);
      }
      
      .dashboard-shell.nav-collapsed .nav-item-label,
      .dashboard-shell.nav-collapsed .sidebar-nav-title {
        display: none;
      }
      
      .sidebar-backdrop {
        display: none;
      }
      
      /* Provisioning UI */
      .provisioning {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        min-height: 100vh;
        gap: var(--space-6);
        padding: var(--space-8);
        text-align: center;
      }
      
      .provisioning-icon {
        font-size: 72px;
        animation: pulse 2s ease-in-out infinite;
      }
      
      .provisioning-title {
        font-size: var(--font-2xl);
        font-weight: 700;
        color: var(--rax-yellow);
        text-shadow: 0 0 20px var(--accent-glow);
      }
      
      .provisioning-subtitle {
        color: var(--text-secondary);
        font-size: var(--font-md);
        max-width: 400px;
      }
      
      .provisioning-steps {
        display: flex;
        flex-direction: column;
        gap: var(--space-3);
        width: 100%;
        max-width: 400px;
      }
      
      .prov-step {
        display: flex;
        align-items: center;
        gap: var(--space-3);
        padding: var(--space-3) var(--space-4);
        background: var(--bg-secondary);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
        font-size: var(--font-sm);
        color: var(--text-tertiary);
        transition: all 0.4s ease;
      }
      
      .prov-step.active {
        border-color: var(--rax-yellow);
        color: var(--rax-yellow);
        background: rgba(255, 214, 0, 0.05);
      }
      
      .prov-step.done {
        border-color: var(--success);
        color: var(--success);
      }
      
      .prov-step.error {
        border-color: var(--error);
        color: var(--error);
      }
      
      .prov-step-icon {
        width: 24px;
        text-align: center;
        font-size: 16px;
      }
      
      .prov-step.active .prov-step-icon {
        animation: spin 1s linear infinite;
      }
      
      @keyframes spin {
        from {
          transform: rotate(0deg);
        }
        to {
          transform: rotate(360deg);
        }
      }
      
      .provisioning-progress {
        width: 100%;
        max-width: 400px;
        height: 4px;
        background: var(--bg-elevated);
        border-radius: 2px;
        overflow: hidden;
      }
      
      .provisioning-progress-bar {
        height: 100%;
        background: var(--rax-yellow);
        border-radius: 2px;
        transition: width 0.6s ease;
        box-shadow: 0 0 10px var(--accent-glow);
      }
      
      .start-chatting-btn {
        padding: var(--space-4) var(--space-8);
        background: var(--rax-yellow);
        color: var(--rax-black);
        border: none;
        border-radius: var(--radius-lg);
        font-size: var(--font-lg);
        font-weight: 700;
        cursor: pointer;
        transition: all var(--transition-fast);
        box-shadow: 0 0 20px var(--accent-glow);
      }
      
      .start-chatting-btn:hover {
        background: var(--rax-gold);
        transform: scale(1.05);
        box-shadow: 0 0 30px var(--accent-glow);
      }
      
      @media (max-width: 860px) {
        .dashboard-shell {
          grid-template-columns: minmax(0, 1fr);
        }
      
        .dashboard-shell.nav-collapsed {
          grid-template-columns: minmax(0, 1fr);
        }
      
        .sidebar-nav {
          position: fixed;
          left: 0;
          top: var(--app-header-height);
          bottom: 0;
          width: min(80vw, 280px);
          z-index: 40;
          transform: translateX(-110%);
          transition: transform var(--transition-fast);
          box-shadow: var(--shadow-lg);
        }
      
        .dashboard-shell.nav-collapsed .nav-item {
          justify-content: flex-start;
          padding: 10px 11px;
        }
      
        .dashboard-shell.nav-collapsed .nav-item-label,
        .dashboard-shell.nav-collapsed .sidebar-nav-title {
          display: block;
        }
      
        .dashboard-shell.nav-open .sidebar-nav {
          transform: translateX(0);
        }
      
        .sidebar-backdrop {
          display: block;
          position: fixed;
          inset: var(--app-header-height) 0 0 0;
          z-index: 30;
          background: rgba(0, 0, 0, 0.45);
          opacity: 0;
          pointer-events: none;
          transition: opacity var(--transition-fast);
        }
      
        .dashboard-shell.nav-open .sidebar-backdrop {
          opacity: 1;
          pointer-events: all;
        }
      }
      
      @media (max-width: 640px) {
        :host {
          --app-header-height: 58px;
        }
      
        .app-header {
          padding: var(--space-2) var(--space-3);
          gap: var(--space-2);
        }
      
        .logo {
          font-size: var(--font-lg);
        }
      
        .logo-icon {
          font-size: 22px;
        }
      
        .header-actions {
          gap: var(--space-2);
        }
      
        .user-menu {
          padding: var(--space-1) var(--space-2);
        }
      
        .user-avatar {
          width: 24px;
          height: 24px;
          font-size: 10px;
        }
      
        .user-name {
          display: none;
        }
      
        .dashboard-summary {
          padding: var(--space-4);
          gap: var(--space-3);
        }
      }

      /* Split View Layout */
      .split-view-container {
        display: flex;
        flex: 1;
        min-height: 0;
        overflow: hidden;
      }

      .split-panel {
        display: flex;
        flex-direction: column;
        min-height: 0;
        overflow: hidden;
        transition: flex 0.3s ease, opacity 0.2s ease;
      }

      .split-panel.office-panel {
        background: var(--bg-secondary);
        position: relative;
      }

      .split-panel.chat-panel {
        background: var(--bg-primary);
      }

      /* Collapsed states */
      .split-panel.collapsed {
        flex: 0 0 60px !important;
        min-width: 60px;
        max-width: 60px;
      }

      .split-panel.collapsed.office-panel {
        background: var(--bg-elevated);
      }

      .split-panel.hidden {
        flex: 0 0 0 !important;
        min-width: 0;
        max-width: 0;
        opacity: 0;
        overflow: hidden;
      }

      /* Collapsed office strip */
      .collapsed-office-strip {
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: var(--space-3) var(--space-2);
        gap: var(--space-3);
        height: 100%;
      }

      .collapsed-agent-icon {
        width: 40px;
        height: 40px;
        border-radius: 50%;
        background: var(--bg-secondary);
        border: 2px solid var(--border-color);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 18px;
        cursor: pointer;
        transition: all var(--transition-fast);
      }

      .collapsed-agent-icon:hover {
        border-color: var(--rax-yellow);
        transform: scale(1.1);
      }

      .collapsed-agent-icon.active {
        border-color: var(--rax-yellow);
        box-shadow: 0 0 8px var(--accent-glow);
      }

      /* Divider with collapse buttons */
      .split-divider-wrapper {
        position: relative;
        display: flex;
        align-items: center;
      }

      .divider-collapse-btn {
        position: absolute;
        width: 20px;
        height: 32px;
        background: var(--bg-elevated);
        border: 1px solid var(--border-color);
        border-radius: 4px;
        color: var(--text-secondary);
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 10;
        transition: all var(--transition-fast);
      }

      .divider-collapse-btn:hover {
        background: var(--bg-secondary);
        color: var(--rax-yellow);
        border-color: var(--rax-yellow);
      }

      .divider-collapse-btn.left {
        left: -22px;
      }

      .divider-collapse-btn.right {
        right: -22px;
      }

      .divider-collapse-btn svg {
        width: 12px;
        height: 12px;
        stroke: currentColor;
        fill: none;
        stroke-width: 2px;
      }

      /* Expand buttons when collapsed */
      .expand-panel-btn {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        width: 24px;
        height: 48px;
        background: var(--bg-elevated);
        border: 1px solid var(--border-color);
        border-radius: 4px;
        color: var(--text-secondary);
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 20;
        transition: all var(--transition-fast);
      }

      .expand-panel-btn:hover {
        background: var(--bg-secondary);
        color: var(--rax-yellow);
        border-color: var(--rax-yellow);
      }

      .expand-panel-btn.expand-office {
        left: 0;
        border-left: none;
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
      }

      .expand-panel-btn.expand-chat {
        right: 0;
        border-right: none;
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
      }

      .expand-panel-btn svg {
        width: 14px;
        height: 14px;
        stroke: currentColor;
        fill: none;
        stroke-width: 2px;
      }

      /* Mobile responsive split view */
      @media (max-width: 768px) {
        .split-view-container {
          flex-direction: column;
        }

        .split-panel.office-panel {
          flex: 0 0 200px !important;
          max-height: 200px;
          min-height: 150px;
        }

        .split-panel.chat-panel {
          flex: 1 !important;
        }

        .split-panel.collapsed.office-panel {
          flex: 0 0 50px !important;
          max-height: 50px;
          min-height: 50px;
          max-width: none;
          min-width: 0;
        }

        .split-panel.hidden {
          max-height: 0;
          max-width: none;
        }

        .collapsed-office-strip {
          flex-direction: row;
          padding: var(--space-2) var(--space-3);
          gap: var(--space-2);
          height: auto;
          width: 100%;
          overflow-x: auto;
        }

        .collapsed-agent-icon {
          width: 32px;
          height: 32px;
          font-size: 14px;
          flex-shrink: 0;
        }

        .split-divider-wrapper {
          width: 100%;
          height: 4px;
          flex-direction: row;
        }

        resizable-divider {
          width: 100% !important;
          height: 4px !important;
          cursor: row-resize !important;
        }

        .divider-collapse-btn {
          width: 32px;
          height: 20px;
        }

        .divider-collapse-btn.left {
          left: 50%;
          transform: translateX(-70%);
          top: -22px;
        }

        .divider-collapse-btn.right {
          left: 50%;
          transform: translateX(10%);
          top: -22px;
        }

        .expand-panel-btn {
          width: 48px;
          height: 24px;
          top: 0;
          transform: none;
        }

        .expand-panel-btn.expand-office {
          left: 50%;
          transform: translateX(-100%);
          top: auto;
          bottom: 0;
          border-bottom: none;
          border-left: 1px solid var(--border-color);
          border-top-left-radius: 4px;
          border-bottom-left-radius: 0;
          border-bottom-right-radius: 0;
        }

        .expand-panel-btn.expand-chat {
          left: 50%;
          top: 0;
          right: auto;
          border-top: none;
          border-right: 1px solid var(--border-color);
          border-top-right-radius: 0;
          border-bottom-right-radius: 4px;
          border-top-left-radius: 0;
        }
      }

      /* Delegation Indicator - shows which band members are working */
      .delegation-indicator {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        padding: var(--space-2) var(--space-3);
        background: linear-gradient(135deg, rgba(255, 208, 0, 0.1), rgba(255, 161, 0, 0.05));
        border-bottom: 1px solid rgba(255, 208, 0, 0.2);
        font-size: var(--font-xs);
        animation: fadeIn 0.2s ease-out;
      }

      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }

      .delegation-icons {
        font-size: 14px;
        letter-spacing: -2px;
      }

      .delegation-text {
        color: var(--text-secondary);
      }

      .delegation-workers {
        background: rgba(255, 208, 0, 0.2);
        color: var(--rax-yellow);
        padding: 2px 6px;
        border-radius: var(--radius-sm);
        font-weight: 500;
      }
    `,
  ];

  @state()
  private view: AppView = "loading";

  @state()
  private user: UserInfo | null = null;

  @state()
  private onboardingStatus: OnboardingStatus | null = null;

  @state()
  private agents: Agent[] = [];

  @state()
  private error: string | null = null;

  @state()
  private showCreateModal = false;

  @state()
  private activeTab:
    | "office"
    | "band"
    | "runtime"
    | "connections"
    | "costs"
    | "audit"
    | "learning"
    | "autonomy"
    | "export" = "office";

  @state()
  private authRequired = false;

  @state()
  private signInPrompted = false;

  @state()
  private selectedAgentId: string | null = null;

  @state()
  private showUserMenu = false;

  @state()
  private navCollapsed = false;

  @state()
  private mobileNavOpen = false;

  @state()
  private provisioningStatus = "";

  @state()
  private provisioningStep = 0;

  @state()
  private statusBarData: StatusBarData = {
    sessionCostUsd: 0,
    turnCostUsd: 0,
    turnActionCount: 0,
    status: "idle",
  };

  @state()
  private currentActivityAgentId: string | null = null;

  @state()
  private currentActivityTool: string | null = null;

  @state()
  private onboardingAgentId: string | null = null;

  @state()
  private onboardingAgentName: string = "";

  @state()
  private integrationDraft: IntegrationSetupDraft | null = null;

  @state()
  private secrets: SecretInfo[] = [];

  // Split view state
  @state()
  private splitRatio = 0.4; // Default 40% office, 60% chat

  @state()
  private officeCollapsed = false; // Office collapsed to icon strip

  @state()
  private chatCollapsed = false; // Chat hidden, office full width

  @state()
  private activeDelegation: DelegationPlan | null = null; // Current delegation in progress

  @query("#main-office")
  private pixelOffice?: PixelOffice;

  private lastSessionCostUsd = 0;
  private agentStatusPollInterval: ReturnType<typeof setTimeout> | null = null;
  private provisioningTimer: ReturnType<typeof setInterval> | null = null;
  private _boundKeyHandler: ((e: KeyboardEvent) => void) | null = null;
  // Heartbeat timing: faster during active chat, slower when idle
  private static readonly HEARTBEAT_FAST_MS = 10000; // 10s during active chat
  private static readonly HEARTBEAT_SLOW_MS = 30000; // 30s when idle
  private static readonly HEARTBEAT_IDLE_THRESHOLD_MS = 30000; // 30s idle before slowing down
  private currentHeartbeatRate = RaxApp.HEARTBEAT_SLOW_MS;
  private lastActivityTime = 0;

  private getChatSelectionStorageKey(): string {
    const userKey =
      typeof this.user?.id === "string" && this.user.id.trim().length > 0
        ? this.user.id.trim()
        : "anonymous";
    return `rax.chat.lastAgent.${userKey}`;
  }

  private getFirstRunChatStorageKey(): string {
    const userKey =
      typeof this.user?.id === "string" && this.user.id.trim().length > 0
        ? this.user.id.trim()
        : "anonymous";
    return `rax.chat.firstRunSeen.${userKey}`;
  }

  private saveChatSelection(agentId: string | null) {
    if (typeof window === "undefined") {
      return;
    }
    const key = this.getChatSelectionStorageKey();
    if (!agentId) {
      localStorage.removeItem(key);
      return;
    }
    localStorage.setItem(key, agentId);
  }

  private loadChatSelection(): string | null {
    if (typeof window === "undefined") {
      return null;
    }
    const key = this.getChatSelectionStorageKey();
    const value = localStorage.getItem(key);
    return value && value.trim().length > 0 ? value.trim() : null;
  }

  private hasSeenFirstRunChat(): boolean {
    if (typeof window === "undefined") {
      return true;
    }
    const value = localStorage.getItem(this.getFirstRunChatStorageKey());
    return value === "1";
  }

  private markFirstRunChatSeen() {
    if (typeof window === "undefined") {
      return;
    }
    localStorage.setItem(this.getFirstRunChatStorageKey(), "1");
  }

  // Split view storage
  private getSplitRatioStorageKey(): string {
    return "rax.splitView.ratio";
  }

  private getSplitCollapseStorageKey(): string {
    return "rax.splitView.collapse";
  }

  private saveSplitRatio(ratio: number) {
    if (typeof window === "undefined") return;
    localStorage.setItem(this.getSplitRatioStorageKey(), String(ratio));
  }

  private loadSplitRatio(): number {
    if (typeof window === "undefined") return 0.4;
    const value = localStorage.getItem(this.getSplitRatioStorageKey());
    if (value) {
      const parsed = parseFloat(value);
      if (!isNaN(parsed) && parsed >= 0.2 && parsed <= 0.8) {
        return parsed;
      }
    }
    return 0.4;
  }

  private saveSplitCollapseState(state: "none" | "office" | "chat") {
    if (typeof window === "undefined") return;
    localStorage.setItem(this.getSplitCollapseStorageKey(), state);
  }

  private loadSplitCollapseState(): "none" | "office" | "chat" {
    if (typeof window === "undefined") return "none";
    const value = localStorage.getItem(this.getSplitCollapseStorageKey());
    if (value === "office" || value === "chat") {
      return value;
    }
    return "none";
  }

  override async connectedCallback() {
    super.connectedCallback();
    // Restore tab from URL hash
    const hash = window.location.hash.slice(1);
    if (hash && (RaxApp.VALID_TABS as readonly string[]).includes(hash)) {
      this.activeTab = hash as typeof this.activeTab;
    }
    // Listen for back/forward navigation
    this._onHashChange = () => {
      const h = window.location.hash.slice(1);
      if (h && (RaxApp.VALID_TABS as readonly string[]).includes(h)) {
        this.activeTab = h as typeof this.activeTab;
        // Clear agent selection so the tab content is shown instead of chat
        this.selectedAgentId = null;
      } else if (!h) {
        this.activeTab = "office";
      }
    };
    window.addEventListener("hashchange", this._onHashChange);

    // Restore split view state
    this.splitRatio = this.loadSplitRatio();
    const collapseState = this.loadSplitCollapseState();
    this.officeCollapsed = collapseState === "office";
    this.chatCollapsed = collapseState === "chat";

    // Add keyboard shortcut for toggling office panel (Cmd/Ctrl+\)
    this._boundKeyHandler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "\\") {
        e.preventDefault();
        this.toggleOfficePanel();
      }
    };
    window.addEventListener("keydown", this._boundKeyHandler);

    // Listen for persona spawn events from gateway (for pixel office animations)
    this._boundPersonaHandler = (e: Event) => this.handlePersonaSpawned(e as CustomEvent);
    window.addEventListener("persona-spawned", this._boundPersonaHandler);

    // Listen for persona completion events
    this._boundPersonaCompletedHandler = (e: Event) => this.handlePersonaCompleted(e as CustomEvent);
    window.addEventListener("persona-completed", this._boundPersonaCompletedHandler);

    await this.initialize();
  }

  private _onHashChange: (() => void) | null = null;
  private _boundPersonaHandler: ((e: Event) => void) | null = null;
  private _boundPersonaCompletedHandler: ((e: Event) => void) | null = null;

  override disconnectedCallback() {
    super.disconnectedCallback();
    if (this._onHashChange) {
      window.removeEventListener("hashchange", this._onHashChange);
    }
    if (this._boundKeyHandler) {
      window.removeEventListener("keydown", this._boundKeyHandler);
    }
    if (this._boundPersonaHandler) {
      window.removeEventListener("persona-spawned", this._boundPersonaHandler);
    }
    if (this._boundPersonaCompletedHandler) {
      window.removeEventListener("persona-completed", this._boundPersonaCompletedHandler);
    }
    this.stopProvisioningPoll();
    this.stopHeartbeatStatusCheck();
  }

  private stopProvisioningPoll() {
    if (this.provisioningTimer) {
      clearInterval(this.provisioningTimer);
      this.provisioningTimer = null;
    }
  }

  private startProvisioningPoll() {
    this.stopProvisioningPoll();
    this.provisioningStep = 1;
    this.provisioningStatus = "provisioning";

    // Animate through steps while polling
    const stepTimer = setInterval(() => {
      if (this.provisioningStep < 4 && this.provisioningStatus === "provisioning") {
        this.provisioningStep++;
      }
    }, 5000);

    this.provisioningTimer = setInterval(async () => {
      try {
        const res = await raxApi.getInstance();
        if (res.success && res.data) {
          const status = res.data.status;
          if (status === "running") {
            this.provisioningStatus = "running";
            this.provisioningStep = 5;
            this.stopProvisioningPoll();
            clearInterval(stepTimer);
          } else if (status === "error") {
            this.provisioningStatus = "error";
            this.stopProvisioningPoll();
            clearInterval(stepTimer);
          }
        }
      } catch (err) {
        console.warn("[RaxApp] Polling error:", err);
      }
    }, 3000);
  }

  private async initialize() {
    try {
      // Check if we have a token (from Clerk or elsewhere)
      await this.initializeAuth();

      // Handle return from Stripe checkout
      if (typeof window !== "undefined") {
        const params = new URL(window.location.href).searchParams;
        if (params.get("checkout") === "success") {
          console.log("[RaxApp] Returned from successful checkout, attempting provision");
          // Clean up URL
          window.history.replaceState({}, "", window.location.pathname);
          // Try to provision now that subscription is active
          try {
            const instanceRes = await raxApi.createInstance();
            if (instanceRes.success) {
              this.view = "provisioning";
              this.startProvisioningPoll();
              return;
            }
          } catch (err) {
            console.warn("[RaxApp] Post-checkout provision failed:", err);
          }
        } else if (params.get("checkout") === "cancel") {
          window.history.replaceState({}, "", window.location.pathname);
        }
      }

      // If user explicitly requested sign-in (e.g., via ?signin=1) AND not already authenticated, open Clerk
      const forceClerkSignIn =
        typeof window !== "undefined" &&
        new URL(window.location.href).searchParams.get("signin") === "1";
      if (forceClerkSignIn) {
        // First check if user is already authenticated
        const isAuthenticated = !!raxApi.getToken();
        if (!isAuthenticated) {
          await this.promptClerkSignIn(true);
        } else {
          // Remove signin parameter from URL since user is already authenticated
          const url = new URL(window.location.href);
          url.searchParams.delete("signin");
          window.history.replaceState({}, "", url.toString());
        }
      }

      // Get user info and onboarding status
      const [userResult, onboardingResult] = await Promise.all([
        raxApi.getCurrentUser(),
        raxApi.getOnboardingStatus(),
      ]);

      // If backend rejected auth, stop and show sign-in prompt
      if (!userResult.success) {
        this.authRequired = true;
        this.promptClerkSignIn();
        this.view = "loading";
        return;
      }

      if (userResult.success && userResult.data) {
        this.user = userResult.data;
      }

      if (onboardingResult.success && onboardingResult.data) {
        this.onboardingStatus = onboardingResult.data;

        // Determine initial view
        if (this.onboardingStatus.status !== "completed") {
          this.view = "onboarding";
        } else {
          // Check if there's a provisioning instance
          try {
            const instanceRes = await raxApi.getInstance();
            if (instanceRes.success && instanceRes.data?.status === "provisioning") {
              this.view = "provisioning";
              this.startProvisioningPoll();
            } else {
              await this.loadDashboard();
              this.view = "dashboard";
            }
          } catch {
            await this.loadDashboard();
            this.view = "dashboard";
          }
        }
      } else {
        // No onboarding status means new user
        this.view = "onboarding";
      }
    } catch (err) {
      console.error("[RaxApp] Initialization error:", err);
      this.error = "Failed to connect to Rax. Please try again.";
      this.view = "loading"; // Shows error state
    }
  }

  private async initializeAuth() {
    // Try to get token from Clerk if available
    if (typeof window !== "undefined") {
      // Wait for Clerk instance to be ready (stored on window.clerkClient by rax.html)
      const clerk = await this.waitForClerk();
      if (clerk?.session) {
        try {
          const token = await clerk.session.getToken();
          if (token) {
            raxApi.setToken(token);
            return;
          }
        } catch {
          // Clerk not ready, will retry
        }
      }

      // If Clerk is present but no active session, prompt sign-in
      if (clerk && !clerk.session?.id) {
        await this.promptClerkSignIn(true);
      }
    }
  }

  private async loadDashboard() {
    console.log("[RaxApp] loadDashboard called");
    const result = await raxApi.listAgents();
    console.log("[RaxApp] listAgents result:", JSON.stringify(result));
    if (result.success && result.data) {
      this.agents = result.data;
      console.log("[RaxApp] agents set:", this.agents.length, "agents");

      // Check if we have an explicit hash tab - if so, don't auto-select an agent
      const hashTab = typeof window !== "undefined" ? window.location.hash.slice(1) : "";
      const hasExplicitHashTab =
        hashTab.length > 0 && (RaxApp.VALID_TABS as readonly string[]).includes(hashTab);

      const currentSelection = this.selectedAgentId;
      const currentStillExists =
        typeof currentSelection === "string" &&
        this.agents.some((agent) => agent.id === currentSelection);
      if (currentSelection && !currentStillExists) {
        this.selectedAgentId = null;
      }

      // Only restore agent selection from localStorage if no explicit hash tab
      // This allows URL routing like /app/#runtime to show the correct tab
      if (!hasExplicitHashTab) {
        const restoredSelection = this.loadChatSelection();
        const restoredExists =
          typeof restoredSelection === "string" &&
          this.agents.some((agent) => agent.id === restoredSelection);
        if (!this.selectedAgentId && restoredExists) {
          this.selectedAgentId = restoredSelection;
        }

        if (this.selectedAgentId) {
          this.saveChatSelection(this.selectedAgentId);
        } else if (!restoredExists) {
          this.saveChatSelection(null);
        }

        // First-run logic: auto-open chat only if no hash tab and first visit
        if (
          !this.selectedAgentId &&
          this.activeTab === "band" &&
          this.agents.length > 0 &&
          !this.hasSeenFirstRunChat()
        ) {
          const firstActiveAgent =
            this.agents.find((agent) => agent.status === "active") || this.agents[0];
          this.selectedAgentId = firstActiveAgent.id;
          this.saveChatSelection(this.selectedAgentId);
          this.markFirstRunChatSeen();
        }
      }
    } else {
      console.warn("[RaxApp] listAgents failed or no data");
    }

    // Start heartbeat status check now that we're authenticated
    this.startHeartbeatStatusCheck();
  }

  override render() {
    if (this.authRequired) {
      return this.renderAuthRequired();
    }
    if (this.error) {
      return this.renderError();
    }

    switch (this.view) {
      case "loading":
        return this.renderLoading();
      case "onboarding":
        return this.renderOnboarding();
      case "provisioning":
        return this.renderProvisioning();
      case "dashboard":
        return this.renderDashboard();
      default:
        return this.renderLoading();
    }
  }

  private renderLoading() {
    return html`
      <div class="loading">
        <div class="loading-logo">⚡</div>
        <div class="loading-text">Loading your band...</div>
      </div>
    `;
  }

  private renderError() {
    return html`
      <div class="error">
        <div class="error-icon">⚠️</div>
        <h2 class="error-title">Connection Error</h2>
        <p class="error-message">${this.error}</p>
        <button class="retry-btn" @click=${this.handleRetry}>
          Try Again
        </button>
      </div>
    `;
  }

  private renderAuthRequired() {
    return html`
      <div class="error">
        <div class="error-icon">🔒</div>
        <h2 class="error-title">Sign in to continue</h2>
        <p class="error-message">
          Please sign in with your Clerk account to access your band. If you just signed up,
          click below to open the sign-in dialog.
        </p>
        <button class="retry-btn" @click=${this.openClerkSignIn}>
          Sign in with Clerk
        </button>
      </div>
    `;
  }

  private renderOnboarding() {
    return html`
      <rax-onboarding
        @onboarding-complete=${this.handleOnboardingComplete}
        @start-chatting=${this.handleStartChatting}
      ></rax-onboarding>
    `;
  }

  private renderProvisioning() {
    const steps = [
      { label: "Creating your cloud instance", icon: "☁️" },
      { label: "Configuring environment", icon: "⚙️" },
      { label: "Deploying your AI agent", icon: "🚀" },
      { label: "Starting services", icon: "⚡" },
      { label: "Ready!", icon: "✅" },
    ];

    const isError = this.provisioningStatus === "error";
    const isReady = this.provisioningStatus === "running";
    const progressPct = isReady ? 100 : isError ? 0 : Math.min(90, this.provisioningStep * 20);

    return html`
      <div class="provisioning">
        <div class="provisioning-icon">${isError ? "⚠️" : isReady ? "🎉" : "⚡"}</div>
        <h2 class="provisioning-title">
          ${isError ? "Setup Error" : isReady ? "Your Agent is Live!" : "Setting Up Your Agent"}
        </h2>
        <p class="provisioning-subtitle">
          ${
            isError
              ? "Something went wrong during setup. Please try again."
              : isReady
                ? "Your personal AI agent is ready and waiting for you."
                : "We're spinning up your personal AI instance. This usually takes about 30 seconds."
          }
        </p>

        <div class="provisioning-progress">
          <div class="provisioning-progress-bar" style="width: ${progressPct}%"></div>
        </div>

        <div class="provisioning-steps">
          ${steps.map((step, i) => {
            const stepNum = i + 1;
            const isDone = this.provisioningStep > stepNum;
            const isActive = this.provisioningStep === stepNum && !isError && !isReady;
            const isStepError = isError && this.provisioningStep === stepNum;
            return html`
              <div class="prov-step ${isDone ? "done" : ""} ${isActive ? "active" : ""} ${isStepError ? "error" : ""}">
                <span class="prov-step-icon">
                  ${isDone ? "✓" : isActive ? "⟳" : isStepError ? "✗" : step.icon}
                </span>
                <span>${step.label}</span>
              </div>
            `;
          })}
        </div>

        ${
          isReady
            ? html`
          <button class="start-chatting-btn" @click=${this.handleStartChattingWithAgent}>
            📊 Go to Dashboard
          </button>
        `
            : ""
        }

        ${
          isError
            ? html`
          <button class="retry-btn" @click=${this.handleRetryProvisioning}>
            Try Again
          </button>
        `
            : ""
        }
      </div>
    `;
  }

  private async handleProvisioningDone() {
    await this.loadDashboard();
    this.view = "dashboard";
  }

  private async handleStartChattingWithAgent() {
    await this.loadDashboard();
    this.view = "dashboard";
  }

  private async handleRetryProvisioning() {
    try {
      // Delete the errored instance and create a new one
      await raxApi.deleteInstance();
      const res = await raxApi.createInstance();
      if (res.success) {
        this.startProvisioningPoll();
      }
    } catch (err) {
      console.error("[RaxApp] Retry provisioning error:", err);
    }
  }

  private renderDashboard() {
    // If onboarding a new agent, show the onboarding wizard
    if (this.onboardingAgentId) {
      return html`
        <div class="app-layout">
          ${this.renderHeader()}
          <main class="app-main">
            <agent-onboarding
              .agentId=${this.onboardingAgentId}
              .agentName=${this.onboardingAgentName}
              .resources=${[]}
              @connect-resource=${this.handleConnectResource}
              @skip-onboarding=${this.handleSkipOnboarding}
              @onboarding-complete=${this.handleAgentOnboardingComplete}
            ></agent-onboarding>
          </main>
        </div>
        ${
          this.integrationDraft
            ? html`
          <rax-integration-setup-popup
            .draft=${this.integrationDraft}
            @cancel=${() => (this.integrationDraft = null)}
            @saved=${this.handleIntegrationSaved}
          ></rax-integration-setup-popup>
        `
            : ""
        }
      `;
    }

    // If chatting with an agent, show split view: office (left) + chat (right)
    if (this.selectedAgentId) {
      return this.renderSplitView();
    }

    return html`
      <div class="app-layout" @click=${this.handleOutsideClick}>
        ${this.renderHeader()}
        <div
          class="dashboard-shell ${this.navCollapsed ? "nav-collapsed" : ""} ${this.mobileNavOpen ? "nav-open" : ""}"
        >
          ${this.renderSidebarNav()}
          <div class="sidebar-backdrop" @click=${this.handleCloseMobileNav}></div>
          <main class="app-main dashboard-main">
            ${this.activeTab === "office" ? this.renderOfficeView() : ""}
            ${this.activeTab === "band" ? this.renderBandView() : ""}
            ${this.activeTab === "runtime" ? this.renderRailwayView() : ""}
            ${
              this.activeTab === "connections"
                ? html`
                    <rax-connections-manager></rax-connections-manager>
                  `
                : ""
            }
            ${
              this.activeTab === "costs"
                ? html`
                    <rax-spend-tracker mode="dashboard"></rax-spend-tracker>
                  `
                : ""
            }
            ${
              this.activeTab === "audit"
                ? html`
                    <rax-audit-trail></rax-audit-trail>
                  `
                : ""
            }
            ${
              this.activeTab === "learning"
                ? html`
                    <rax-learning-ledger></rax-learning-ledger>
                  `
                : ""
            }
            ${
              this.activeTab === "autonomy"
                ? html`
                    <rax-autonomy-settings></rax-autonomy-settings>
                  `
                : ""
            }
            ${
              this.activeTab === "export"
                ? html`
                    <rax-data-export></rax-data-export>
                  `
                : ""
            }
          </main>
        </div>
        <rax-status-bar
          .data=${this.statusBarData}
          @show-audit-feed=${this.handleShowAuditFromStatus}
          @show-cost-breakdown=${this.handleShowCostsFromStatus}
          @show-turn-details=${this.handleShowAuditFromStatus}
        ></rax-status-bar>
      </div>
      
      ${
        this.showCreateModal
          ? html`
        <rax-create-agent-modal
          .open=${true}
          @create-agent=${this.handleCreateAgent}
          @close=${() => (this.showCreateModal = false)}
        ></rax-create-agent-modal>
      `
          : ""
      }
      `;
  }

  private handleOutsideClick(e: Event) {
    // Close user menu when clicking outside
    const target = e.target as HTMLElement;
    if (!target.closest(".user-menu-container")) {
      this.showUserMenu = false;
    }
    if (
      this.mobileNavOpen &&
      !target.closest(".sidebar-nav") &&
      !target.closest(".sidebar-toggle")
    ) {
      this.mobileNavOpen = false;
    }
  }

  private handleCloseChat() {
    this.selectedAgentId = null;
    this.mobileNavOpen = false;
    this.lastSessionCostUsd = 0;
    this.statusBarData = {
      ...this.statusBarData,
      status: "idle",
      turnCostUsd: 0,
      turnActionCount: 0,
    };
    // Switch to band/dashboard tab so sidebar nav is visible
    this.setActiveTab("band");
  }

  private openClerkSignIn = async () => {
    await this.promptClerkSignIn(true);
  };

  private async waitForClerk(): Promise<any | null> {
    // Prefer the ready promise set in rax.html
    // @ts-ignore
    const ready = (window as any).__clerkReady;
    if (ready && typeof ready.then === "function") {
      try {
        const clerk = await ready;
        if (clerk && typeof clerk.openSignIn === "function") {
          return clerk;
        }
      } catch (err) {
        console.warn("[RaxApp] Clerk ready promise failed", err);
      }
    }

    const maxAttempts = 20;
    const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
    for (let i = 0; i < maxAttempts; i++) {
      // @ts-ignore
      const clerkClient = (window as any).clerkClient;
      if (clerkClient && typeof clerkClient.openSignIn === "function") {
        return clerkClient;
      }
      // @ts-ignore keep backward compatibility if instance ended up on window.Clerk
      const maybeInstance = (window as any).Clerk;
      if (maybeInstance && typeof maybeInstance.openSignIn === "function") {
        return maybeInstance;
      }
      await delay(150);
    }
    return null;
  }

  private async promptClerkSignIn(force = false) {
    if (this.signInPrompted && !force) return;
    const Clerk = await this.waitForClerk();
    if (!Clerk) {
      this.signInPrompted = false;
      return;
    }
    this.signInPrompted = true;
    try {
      await Clerk.openSignIn({
        fallbackRedirectUrl: "/app",
        signUpFallbackRedirectUrl: "/app",
      });
      const token = await Clerk.session?.getToken();
      if (token) {
        raxApi.setToken(token);
        this.authRequired = false;
        this.signInPrompted = false;
        await this.initialize();
      } else {
        this.authRequired = true;
      }
    } catch (err) {
      console.warn("[RaxApp] Clerk sign-in failed", err);
      this.authRequired = true;
    }
  }

  private renderHeader() {
    const showSidebarToggle = this.view === "dashboard" && !this.selectedAgentId;
    return html`
      <header class="app-header">
        ${
          showSidebarToggle
            ? html`
              <button
                class="sidebar-toggle"
                @click=${this.handleToggleSidebar}
                title="Toggle navigation"
                aria-label="Toggle navigation"
              >
                ${icons.menu}
              </button>
            `
            : ""
        }
        <div class="logo" style="cursor: pointer" @click=${this.handleGoHome}>
          <span class="logo-icon">⚡</span>
          <span>Rax</span>
        </div>
        <div class="header-actions">
          ${
            this.user
              ? html`
            <div class="user-menu-container">
              <div class="user-menu" @click=${() => (this.showUserMenu = !this.showUserMenu)}>
                <div class="user-avatar">
                  ${this.getInitials(this.user.name || this.user.email || "U")}
                </div>
                <span class="user-name">${this.user.name || this.user.email || "User"}</span>
              </div>
              ${
                this.showUserMenu
                  ? html`
                <div class="user-dropdown">
                  <button class="dropdown-item" @click=${this.handleOpenProfile}>
                    👤 Profile
                  </button>
                  <button class="dropdown-item" @click=${this.handleOpenBilling}>
                    💳 Billing
                  </button>
                  <div class="dropdown-divider"></div>
                  <button class="dropdown-item danger" @click=${this.handleSignOut}>
                    🚪 Sign Out
                  </button>
                </div>
              `
                  : ""
              }
            </div>
          `
              : ""
          }
        </div>
      </header>
    `;
  }

  private handleGoHome() {
    this.selectedAgentId = null;
    this.mobileNavOpen = false;
    this.activeTab = "band";
    this.showUserMenu = false;
  }

  private async handleOpenProfile() {
    this.showUserMenu = false;
    const clerk = await this.waitForClerk();
    if (clerk?.openUserProfile) {
      clerk.openUserProfile();
    }
  }

  private handleOpenBilling() {
    this.showUserMenu = false;
    this.setActiveTab("costs");
  }

  private async handleSignOut() {
    this.showUserMenu = false;
    const clerk = await this.waitForClerk();
    if (clerk?.signOut) {
      await clerk.signOut();
      window.location.reload();
    }
  }

  private renderSidebarNav() {
    // Simplified navigation with logical groupings
    const navGroups: Array<{
      title?: string;
      items: Array<{
        key: typeof this.activeTab;
        icon: keyof typeof icons;
        label: string;
      }>;
    }> = [
      {
        items: [
          { key: "office", icon: "monitor", label: "Office" },
          { key: "band", icon: "radio", label: "Dashboard" },
        ],
      },
      {
        title: "Agent",
        items: [
          { key: "connections", icon: "plug", label: "Channels" },
          { key: "autonomy", icon: "shield", label: "Autonomy" },
          { key: "learning", icon: "brain", label: "Learning" },
        ],
      },
      {
        title: "Monitor",
        items: [
          { key: "costs", icon: "barChart", label: "Spend" },
          { key: "audit", icon: "scrollText", label: "Activity" },
        ],
      },
      {
        title: "System",
        items: [
          { key: "runtime", icon: "monitor", label: "Agent's Computer" },
          { key: "export", icon: "download", label: "Export Data" },
        ],
      },
    ];

    return html`
      <aside class="sidebar-nav">
        ${navGroups.map(
          (group) => html`
            ${group.title ? html`<div class="sidebar-nav-title">${group.title}</div>` : ""}
            ${group.items.map(
              (item) => html`
                <button
                  class="nav-item ${this.activeTab === item.key ? "active" : ""}"
                  @click=${() => this.setActiveTab(item.key)}
                  title=${item.label}
                >
                  <span class="nav-item-icon">${icons[item.icon]}</span>
                  <span class="nav-item-label">${item.label}</span>
                </button>
              `,
            )}
          `,
        )}
      </aside>
    `;
  }

  private static readonly VALID_TABS = [
    "office",
    "band",
    "runtime",
    "connections",
    "costs",
    "audit",
    "learning",
    "autonomy",
    "export",
  ] as const;

  private setActiveTab(
    tab:
      | "office"
      | "band"
      | "runtime"
      | "connections"
      | "costs"
      | "audit"
      | "learning"
      | "autonomy"
      | "export",
  ) {
    this.activeTab = tab;
    this.mobileNavOpen = false;
    // Update URL hash for bookmarkable routing (all tabs including office)
    if (window.location.hash.slice(1) !== tab) {
      history.replaceState(null, "", `#${tab}`);
    }
  }

  private handleToggleSidebar = () => {
    if (window.matchMedia("(max-width: 860px)").matches) {
      this.mobileNavOpen = !this.mobileNavOpen;
      return;
    }
    this.navCollapsed = !this.navCollapsed;
  };

  private handleCloseMobileNav = () => {
    this.mobileNavOpen = false;
  };

  private renderDashboardSummary() {
    const activeAgents = this.agents.filter((a) => a.status === "active").length;
    const totalSpend = this.agents.reduce((sum, a) => sum + (a.costThisMonth || 0), 0);

    return html`
      <div class="dashboard-summary">
        <div class="summary-card">
          <div class="summary-label">Total Spend This Month</div>
          <div class="summary-value">$${totalSpend.toFixed(2)}</div>
          <div class="summary-subtext">Across all agents</div>
        </div>
        <div class="summary-card">
          <div class="summary-label">Active Agents</div>
          <div class="summary-value green">${activeAgents}</div>
          <div class="summary-subtext">of ${this.agents.length} total</div>
        </div>
        <div class="summary-card">
          <div class="summary-label">Messages Today</div>
          <div class="summary-value">0</div>
          <div class="summary-subtext">Start chatting!</div>
        </div>
        <div class="summary-card">
          <div class="summary-label">Status</div>
          <div class="summary-value green">●</div>
          <div class="summary-subtext">All systems operational</div>
        </div>
      </div>
    `;
  }

  private renderBandView() {
    return html`
      ${this.renderDashboardSummary()}
      <rax-band-dashboard
        .band=${this.agents}
        @add-agent=${() => (this.showCreateModal = true)}
        @select-agent=${this.handleSelectAgent}
        @open-chat=${this.handleOpenChat}
        @open-settings=${this.handleOpenSettings}
        @pause-agent=${this.handlePauseAgent}
        @resume-agent=${this.handleResumeAgent}
      ></rax-band-dashboard>
    `;
  }

  private renderRailwayView() {
    return html`
      <railway-dashboard></railway-dashboard>
    `;
  }

  private renderOfficeView() {
    // Map agents to pixel office format
    const officeAgents = this.agents.map((agent) => ({
      id: agent.id,
      name: agent.name,
      palette: Math.abs(agent.id.charCodeAt(0) % 5), // Deterministic palette from ID
    }));

    return html`
      <div style="height: 100%; display: flex; flex-direction: column;">
        <pixel-office
          id="main-office"
          .agents=${officeAgents}
          ?showBandMembers=${true}
          style="flex: 1; min-height: 400px;"
          @agent-selected=${(e: CustomEvent) => this.handleOpenChat(e)}
          @band-member-woke=${(e: CustomEvent) => this.handleBandMemberWoke(e)}
          @band-delegation=${(e: CustomEvent) => this.handleBandDelegation(e)}
        ></pixel-office>
      </div>
    `;
  }

  /**
   * Renders the split view with pixel-office on left and agent-chat on right.
   * Includes resizable divider and collapse/expand functionality.
   */
  private renderSplitView() {
    const agent = this.agents.find((a) => a.id === this.selectedAgentId);
    const officeAgents = this.agents.map((a) => ({
      id: a.id,
      name: a.name,
      palette: Math.abs(a.id.charCodeAt(0) % 5),
    }));

    // Calculate panel styles based on split ratio and collapse state
    const officeFlex = this.chatCollapsed
      ? "1"
      : this.officeCollapsed
        ? "0 0 60px"
        : `0 0 ${this.splitRatio * 100}%`;
    const chatFlex = this.chatCollapsed
      ? "0 0 0"
      : this.officeCollapsed
        ? "1"
        : `1`;

    // Chevron icons for collapse/expand
    const chevronLeft = html`<svg viewBox="0 0 24 24"><polyline points="15 18 9 12 15 6"></polyline></svg>`;
    const chevronRight = html`<svg viewBox="0 0 24 24"><polyline points="9 18 15 12 9 6"></polyline></svg>`;
    const chevronUp = html`<svg viewBox="0 0 24 24"><polyline points="18 15 12 9 6 15"></polyline></svg>`;
    const chevronDown = html`<svg viewBox="0 0 24 24"><polyline points="6 9 12 15 18 9"></polyline></svg>`;

    return html`
      <div class="app-layout">
        <div class="split-view-container">
          <!-- Office Panel -->
          <div
            class="split-panel office-panel ${this.officeCollapsed ? "collapsed" : ""} ${this.chatCollapsed ? "" : ""}"
            style="flex: ${officeFlex};"
          >
            ${this.officeCollapsed
              ? this.renderCollapsedOfficeStrip()
              : html`
                  <pixel-office
                    id="main-office"
                    .agents=${officeAgents}
                    ?showBandMembers=${true}
                    style="flex: 1; min-height: 0;"
                    @agent-selected=${(e: CustomEvent) => this.handleAgentSelectedInSplit(e)}
                    @band-member-woke=${(e: CustomEvent) => this.handleBandMemberWoke(e)}
                    @band-delegation=${(e: CustomEvent) => this.handleBandDelegation(e)}
                  ></pixel-office>
                `}
            ${this.chatCollapsed
              ? html`
                  <button
                    class="expand-panel-btn expand-chat"
                    @click=${this.expandChat}
                    title="Show Chat (Cmd+\\)"
                  >
                    ${chevronLeft}
                  </button>
                `
              : ""}
          </div>

          <!-- Resizable Divider with collapse buttons -->
          ${!this.officeCollapsed && !this.chatCollapsed
            ? html`
                <div class="split-divider-wrapper">
                  <button
                    class="divider-collapse-btn left"
                    @click=${this.collapseOffice}
                    title="Collapse Office"
                  >
                    ${chevronLeft}
                  </button>
                  <resizable-divider
                    .splitRatio=${this.splitRatio}
                    .minRatio=${0.2}
                    .maxRatio=${0.8}
                    @resize=${(e: CustomEvent) => this.handleSplitResize(e)}
                  ></resizable-divider>
                  <button
                    class="divider-collapse-btn right"
                    @click=${this.collapseChat}
                    title="Expand Office"
                  >
                    ${chevronRight}
                  </button>
                </div>
              `
            : ""}

          <!-- Chat Panel -->
          <div
            class="split-panel chat-panel ${this.chatCollapsed ? "hidden" : ""}"
            style="flex: ${chatFlex};"
          >
            ${this.officeCollapsed
              ? html`
                  <button
                    class="expand-panel-btn expand-office"
                    @click=${this.expandOffice}
                    title="Show Office (Cmd+\\)"
                  >
                    ${chevronRight}
                  </button>
                `
              : ""}
            ${this.activeDelegation
              ? html`
                  <div class="delegation-indicator">
                    <span class="delegation-icons">
                      ${this.activeDelegation.primary.icon}${this.activeDelegation.supporting.map((s) => s.icon).join("")}
                    </span>
                    <span class="delegation-text">${this.activeDelegation.reasoning}</span>
                    ${this.activeDelegation.ephemeralWorkers > 0
                      ? html`<span class="delegation-workers">+${this.activeDelegation.ephemeralWorkers} workers</span>`
                      : ""}
                  </div>
                `
              : ""}
            <div class="chat-with-activity" style="display: flex; flex: 1; min-height: 0;">
              <rax-agent-chat
                .agentId=${this.selectedAgentId || ""}
                .agentName=${agent?.name || "Agent"}
                .agentIcon=${agent?.icon || "⚡"}
                @back=${this.handleCloseChat}
                @session-cost-update=${this.handleSessionCostUpdate}
                @session-status-change=${this.handleSessionStatusChange}
                @user-message-sent=${this.handleUserMessageSent}
                style="flex: 1; min-height: 0;"
              ></rax-agent-chat>
              <activity-panel></activity-panel>
            </div>
          </div>
        </div>
        <rax-status-bar
          .data=${this.statusBarData}
          @show-audit-feed=${this.handleShowAuditFromStatus}
          @show-cost-breakdown=${this.handleShowCostsFromStatus}
          @show-turn-details=${this.handleShowAuditFromStatus}
        ></rax-status-bar>
      </div>
    `;
  }

  /**
   * Renders the collapsed office strip showing agent icons.
   */
  private renderCollapsedOfficeStrip() {
    return html`
      <div class="collapsed-office-strip">
        ${this.agents.map(
          (agent) => html`
            <div
              class="collapsed-agent-icon ${agent.id === this.selectedAgentId ? "active" : ""}"
              @click=${() => this.handleCollapsedAgentClick(agent.id)}
              title="${agent.name}"
            >
              ${agent.icon || "⚡"}
            </div>
          `,
        )}
      </div>
    `;
  }

  /**
   * Handle clicking on an agent icon in the collapsed strip.
   */
  private handleCollapsedAgentClick(agentId: string) {
    this.selectedAgentId = agentId;
    this.saveChatSelection(agentId);
  }

  /**
   * Handle agent selection from pixel-office in split view.
   * Updates the chat to show the selected agent.
   */
  private handleAgentSelectedInSplit(e: CustomEvent) {
    const agentId = e.detail.agentId;
    if (agentId && agentId !== this.selectedAgentId) {
      this.selectedAgentId = agentId;
      this.saveChatSelection(agentId);
      this.lastSessionCostUsd = 0;
      this.statusBarData = {
        ...this.statusBarData,
        status: "thinking",
        sessionCostUsd: 0,
        turnCostUsd: 0,
        turnActionCount: 0,
      };
    }
  }

  private startHeartbeatStatusCheck() {
    if (this.agentStatusPollInterval) return;
    // Only start if authenticated
    if (!raxApi.getToken()) return;
    // Initial check
    void this.checkAgentStatus();
    // Start with slow heartbeat - will speed up when chat becomes active
    this.currentHeartbeatRate = RaxApp.HEARTBEAT_SLOW_MS;
    this.scheduleNextHeartbeat();
  }

  private scheduleNextHeartbeat() {
    if (this.agentStatusPollInterval) {
      clearTimeout(this.agentStatusPollInterval);
    }
    this.agentStatusPollInterval = setTimeout(() => {
      void this.checkAgentStatus();
      // Determine next heartbeat rate based on activity
      const now = Date.now();
      const timeSinceActivity = now - this.lastActivityTime;
      const shouldBeFast = timeSinceActivity < RaxApp.HEARTBEAT_IDLE_THRESHOLD_MS;
      const newRate = shouldBeFast ? RaxApp.HEARTBEAT_FAST_MS : RaxApp.HEARTBEAT_SLOW_MS;
      if (newRate !== this.currentHeartbeatRate) {
        this.currentHeartbeatRate = newRate;
      }
      this.scheduleNextHeartbeat();
    }, this.currentHeartbeatRate);
  }

  // Call this when chat activity happens to switch to fast heartbeat
  private markChatActive() {
    this.lastActivityTime = Date.now();
    // If currently on slow heartbeat, switch to fast immediately
    if (this.currentHeartbeatRate === RaxApp.HEARTBEAT_SLOW_MS) {
      this.currentHeartbeatRate = RaxApp.HEARTBEAT_FAST_MS;
      this.scheduleNextHeartbeat();
    }
  }

  private stopHeartbeatStatusCheck() {
    if (this.agentStatusPollInterval) {
      clearTimeout(this.agentStatusPollInterval);
      this.agentStatusPollInterval = null;
    }
  }

  private async checkAgentStatus() {
    // Skip if not authenticated
    if (!raxApi.getToken()) return;
    try {
      // Fetch status and secrets in parallel
      const [statusResult, secretsResult] = await Promise.all([
        raxApi.getAgentStatus(),
        raxApi.listSecrets(),
      ]);

      // Update activity status
      if (statusResult.success && statusResult.data) {
        const { isProcessing, currentTool } = statusResult.data;
        const agentId = this.agents[0]?.id;
        if (agentId) {
          this.currentActivityAgentId = isProcessing ? agentId : null;
          this.currentActivityTool = isProcessing ? currentTool : null;
          this.updatePixelOfficeActivity(agentId, isProcessing, currentTool);
        }
      }

      // Update secrets cache and attention states
      if (secretsResult.success && secretsResult.data) {
        this.secrets = secretsResult.data;
        this.updateAgentAttentionStates();
      }
    } catch {
      // Silently ignore errors (including 401s)
    }
  }

  /**
   * Calculate attention count for an agent based on missing integrations and skills.
   * Disabled - agents show activity status instead of setup nag warnings.
   */
  private calculateAgentAttentionCount(_agent: Agent): number {
    // Disabled: no more setup warning bubbles
    // The pixel office now shows agent activity/thinking status instead
    return 0;
  }

  /**
   * Determine if an agent is newly created (no skills, no integrations).
   */
  private isNewlyCreatedAgent(agent: Agent): boolean {
    const hasNoSkills = !agent.enabledSkills || agent.enabledSkills.length === 0;
    const hasNoSecrets = this.secrets.length === 0;
    return hasNoSkills && hasNoSecrets;
  }

  /**
   * Update pixel-office attention states for all agents.
   */
  private updateAgentAttentionStates() {
    if (!this.pixelOffice || this.agents.length === 0) return;

    const attentionStates: Array<{ agentId: string; attentionCount: number }> = [];

    for (const agent of this.agents) {
      // For newly created agents, show setup bubble instead
      if (this.isNewlyCreatedAgent(agent)) {
        this.pixelOffice.showSetup(agent.id);
        continue;
      }

      const count = this.calculateAgentAttentionCount(agent);
      attentionStates.push({ agentId: agent.id, attentionCount: count });
    }

    // Update attention states for non-new agents
    if (attentionStates.length > 0) {
      this.pixelOffice.updateAttentionStates(attentionStates);
    }
  }

  // Reading tools that should show "reading" animation instead of "typing"
  private static readonly READING_TOOLS = new Set([
    "Read",
    "Grep",
    "Glob",
    "WebFetch",
    "WebSearch",
  ]);

  private updatePixelOfficeActivity(agentId: string, isActive: boolean, tool?: string | null) {
    if (this.pixelOffice && agentId) {
      // Map tool to activity type: reading tools show "reading", others show "typing"
      let activityType: "idle" | "typing" | "reading" | "thinking" = "idle";
      if (isActive) {
        if (tool && RaxApp.READING_TOOLS.has(tool)) {
          activityType = "reading";
        } else if (tool) {
          activityType = "typing";
        } else {
          activityType = "thinking";
        }

        // Move agent to task-specific desk based on tool being used
        if (tool) {
          const taskType = detectTaskFromTool(tool);
          if (taskType) {
            this.pixelOffice.moveAgentToTaskDesk(agentId, taskType);
          }
        }
      }
      this.pixelOffice.updateActivity({
        agentId,
        type: activityType,
        tool: tool || undefined,
      });
    }
  }

  // ===========================================================================
  // Split View Handlers
  // ===========================================================================

  private handleSplitResize(e: CustomEvent<{ splitRatio: number }>) {
    const newRatio = e.detail.splitRatio;
    this.splitRatio = newRatio;
    this.saveSplitRatio(newRatio);
  }

  private toggleOfficePanel() {
    if (this.officeCollapsed) {
      // Expand office
      this.officeCollapsed = false;
      this.saveSplitCollapseState("none");
    } else if (this.chatCollapsed) {
      // Restore to split view
      this.chatCollapsed = false;
      this.saveSplitCollapseState("none");
    } else {
      // Collapse office
      this.officeCollapsed = true;
      this.saveSplitCollapseState("office");
    }
  }

  private collapseOffice() {
    this.officeCollapsed = true;
    this.chatCollapsed = false;
    this.saveSplitCollapseState("office");
  }

  private expandOffice() {
    this.officeCollapsed = false;
    this.chatCollapsed = false;
    this.saveSplitCollapseState("none");
  }

  private collapseChat() {
    this.chatCollapsed = true;
    this.officeCollapsed = false;
    this.saveSplitCollapseState("chat");
  }

  private expandChat() {
    this.chatCollapsed = false;
    this.officeCollapsed = false;
    this.saveSplitCollapseState("none");
  }

  // Spend and audit tabs now use <rax-spend-tracker> and <rax-audit-trail> components directly

  // ===========================================================================
  // Helpers
  // ===========================================================================

  private getInitials(name: string): string {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  }

  // ===========================================================================
  // Event Handlers
  // ===========================================================================

  private async handleRetry() {
    this.error = null;
    this.view = "loading";
    await this.initialize();
  }

  private async handleOnboardingComplete(e: CustomEvent) {
    const data = e.detail;
    console.log("[RaxApp] Onboarding complete:", data);

    try {
      // Try to call backend APIs, but don't fail if they error
      // (backend might not be fully wired up yet)
      const results = {
        mode: false,
        apiKey: false,
        agent: false,
      };

      if (data.mode) {
        try {
          const res = await raxApi.setOnboardingMode(data.mode);
          results.mode = res.success;
        } catch (err) {
          console.warn("[RaxApp] Mode API not available:", err);
        }
      }

      if (data.mode === "byok" && data.apiKey && data.apiProvider) {
        try {
          const res = await raxApi.submitApiKey(data.apiProvider, data.apiKey);
          results.apiKey = res.success;
        } catch (err) {
          console.warn("[RaxApp] API key submission not available:", err);
        }
      }

      if (data.firstAgentName) {
        try {
          const res = await raxApi.createFirstAgent(
            data.firstAgentName,
            data.firstAgentType,
            data.firstAgentPersona,
            data.connectedSkills,
          );
          results.agent = res.success;

          // Determine icon based on persona or type
          const getAgentIcon = () => {
            if (data.firstAgentPersona) {
              const personaIcons: Record<string, string> = {
                researcher: "🔬",
                designer: "🎨",
                coder: "💻",
                writer: "✏️",
                assistant: "🤖",
              };
              return personaIcons[data.firstAgentPersona] || "⚡";
            }
            if (data.firstAgentType === "work") return "👔";
            if (data.firstAgentType === "personal") return "🏠";
            return "⚡";
          };

          // If agent was created, add to local state
          if (res.success && res.data) {
            this.agents = [
              {
                id: res.data.agentId || "temp-" + Date.now(),
                name: data.firstAgentName,
                description: `Your ${data.firstAgentPersona || data.firstAgentType || "personal"} AI assistant`,
                icon: getAgentIcon(),
                type: data.firstAgentType || "custom",
                status: "active",
                enabledSkills: data.connectedSkills || [],
                costThisMonth: 0,
              },
            ];
          }
        } catch (err) {
          console.warn("[RaxApp] Agent creation not available:", err);
          // Determine icon for fallback
          const getAgentIcon = () => {
            if (data.firstAgentPersona) {
              const personaIcons: Record<string, string> = {
                researcher: "🔬",
                designer: "🎨",
                coder: "💻",
                writer: "✏️",
                assistant: "🤖",
              };
              return personaIcons[data.firstAgentPersona] || "⚡";
            }
            if (data.firstAgentType === "work") return "👔";
            if (data.firstAgentType === "personal") return "🏠";
            return "⚡";
          };
          // Create a local placeholder agent anyway for demo
          this.agents = [
            {
              id: "demo-" + Date.now(),
              name: data.firstAgentName || "My Agent",
              description: `Your ${data.firstAgentPersona || data.firstAgentType || "personal"} AI assistant`,
              icon: getAgentIcon(),
              type: data.firstAgentType || "custom",
              status: "active",
              enabledSkills: data.connectedSkills || [],
              costThisMonth: 0,
            },
          ];
        }
      }

      // Provision instance for both managed and BYOK modes
      if (data.mode === "managed" || data.mode === "byok") {
        try {
          const instanceRes = await raxApi.createInstance();
          console.log("[RaxApp] Instance creation response:", JSON.stringify(instanceRes));

          if (instanceRes.success) {
            console.log("[RaxApp] Instance provisioning started");
            this.view = "provisioning";
            this.startProvisioningPoll();
            return;
          }

          const resCode = (instanceRes as any).code;

          if (resCode === "SUBSCRIPTION_REQUIRED") {
            // No subscription — redirect to Stripe checkout
            console.log("[RaxApp] Subscription required, redirecting to checkout");
            try {
              const checkoutRes = await raxApi.request<{ url: string }>(
                "POST",
                "/api/billing/checkout",
                {
                  plan: "pro",
                  successUrl: window.location.origin + "/app?checkout=success",
                  cancelUrl: window.location.origin + "/app?checkout=cancel",
                },
              );
              console.log("[RaxApp] Checkout response:", JSON.stringify(checkoutRes));
              if (checkoutRes.success && checkoutRes.data?.url) {
                window.location.href = checkoutRes.data.url;
                return;
              }
            } catch (checkoutErr) {
              console.error("[RaxApp] Checkout redirect failed:", checkoutErr);
            }
            // If checkout redirect failed, show error — don't silently skip
            this.error =
              "A subscription is required to launch your agent. Please try again or contact support.";
            this.view = "dashboard";
            return;
          }

          if (resCode === "RAILWAY_NOT_CONFIGURED") {
            this.error =
              "Agent provisioning is temporarily unavailable. Please try again in a moment.";
            this.view = "dashboard";
            return;
          }

          // Unknown error
          console.error("[RaxApp] Instance creation failed:", instanceRes);
          this.error =
            (instanceRes as any).error || "Failed to create your agent. Please try again.";
          this.view = "dashboard";
          return;
        } catch (err) {
          console.error("[RaxApp] Instance creation error:", err);
          this.error = "Failed to create your agent. Please try again.";
          this.view = "dashboard";
          return;
        }
      }

      console.log("[RaxApp] Onboarding results:", results);

      // Non-provisioning mode — go to dashboard
      this.view = "dashboard";
    } catch (err) {
      console.error("[RaxApp] Onboarding error:", err);
      // Still go to dashboard even on error
      this.view = "dashboard";
    }
  }

  private handleStartChatting() {
    this.view = "dashboard";
  }

  private async handleCreateAgent(e: CustomEvent) {
    const data = e.detail;
    console.log("[RaxApp] Create agent:", data);

    try {
      const result = await raxApi.createAgent({
        name: data.name,
        description: data.description,
        icon: data.icon,
        type: data.type,
        skills: data.skills,
      });

      if (result.success && result.data) {
        this.agents = [...this.agents, result.data];

        // Start agent onboarding for the new agent
        this.onboardingAgentId = result.data.id;
        this.onboardingAgentName = result.data.name;
      }

      this.showCreateModal = false;
    } catch (err) {
      console.error("[RaxApp] Create agent error:", err);
    }
  }

  private handleConnectResource(e: CustomEvent<{ type: ResourceType }>) {
    const resourceType = e.detail.type;
    const integrationId = RESOURCE_TO_INTEGRATION[resourceType];

    if (integrationId) {
      this.integrationDraft = { channelId: integrationId };
    } else {
      console.warn(`[RaxApp] No integration mapping for resource type: ${resourceType}`);
      // Show a message that this integration is coming soon
      alert(`${resourceType.replace(/_/g, " ")} integration coming soon!`);
    }
  }

  private handleSkipOnboarding() {
    console.log("[RaxApp] Skipping agent onboarding");
    this.onboardingAgentId = null;
    this.onboardingAgentName = "";
    this.integrationDraft = null;
  }

  private handleAgentOnboardingComplete(e: CustomEvent) {
    console.log("[RaxApp] Agent onboarding complete:", e.detail);
    const agentId = this.onboardingAgentId;
    this.onboardingAgentId = null;
    this.onboardingAgentName = "";
    this.integrationDraft = null;

    // Optionally open chat with the newly onboarded agent
    if (agentId) {
      this.selectedAgentId = agentId;
      this.saveChatSelection(agentId);
    }
  }

  private handleIntegrationSaved(e: CustomEvent) {
    console.log("[RaxApp] Integration saved:", e.detail);
    this.integrationDraft = null;
    // Could update the resource status here if we tracked it
  }

  private handleSelectAgent(e: CustomEvent) {
    console.log("[RaxApp] Select agent:", e.detail.agentId);
    // Open chat when selecting an agent
    this.selectedAgentId = e.detail.agentId;
    this.saveChatSelection(this.selectedAgentId);
    this.markFirstRunChatSeen();
    this.lastSessionCostUsd = 0;
    this.statusBarData = {
      ...this.statusBarData,
      status: "thinking",
      sessionCostUsd: 0,
      turnCostUsd: 0,
      turnActionCount: 0,
    };
  }

  private handleOpenChat(e: CustomEvent) {
    console.log("[RaxApp] Open chat:", e.detail.agentId);
    this.selectedAgentId = e.detail.agentId;
    this.saveChatSelection(this.selectedAgentId);
    this.markFirstRunChatSeen();
    this.lastSessionCostUsd = 0;
    this.statusBarData = {
      ...this.statusBarData,
      status: "thinking",
      sessionCostUsd: 0,
      turnCostUsd: 0,
      turnActionCount: 0,
    };
  }

  private handleSessionCostUpdate(
    e: CustomEvent<{
      totalCostUsd: number;
      llmCalls: number;
      toolCalls: number;
      completed?: boolean;
    }>,
  ) {
    const sessionCostUsd = e.detail?.totalCostUsd || 0;
    const previousSessionCost = this.lastSessionCostUsd;
    const turnCostUsd =
      sessionCostUsd >= previousSessionCost ? sessionCostUsd - previousSessionCost : sessionCostUsd;
    this.lastSessionCostUsd = sessionCostUsd;

    const totalActions = (e.detail?.llmCalls || 0) + (e.detail?.toolCalls || 0);
    // If completed flag is set, the turn is done - show idle status
    const newStatus = e.detail?.completed ? "idle" : "running";
    this.statusBarData = {
      ...this.statusBarData,
      sessionCostUsd,
      turnCostUsd,
      turnActionCount: totalActions,
      status: newStatus,
    };
  }

  private handleSessionStatusChange(
    e: CustomEvent<{ status: "idle" | "thinking" | "running" | "error"; tool?: string }>,
  ) {
    if (e.detail?.status) {
      this.statusBarData = {
        ...this.statusBarData,
        status: e.detail.status,
      };

      // Mark chat as active to enable fast heartbeat polling
      const isActive = e.detail.status !== "idle";
      if (isActive) {
        this.markChatActive();
      }

      // Update pixel office character activity (main office view)
      if (this.selectedAgentId) {
        this.currentActivityAgentId = isActive ? this.selectedAgentId : null;
        this.currentActivityTool = isActive ? e.detail.tool || null : null;
        this.updatePixelOfficeActivity(this.selectedAgentId, isActive, e.detail.tool);
      }
    }
  }

  private handleShowAuditFromStatus = () => {
    this.selectedAgentId = null;
    this.setActiveTab("audit");
  };

  private handleShowCostsFromStatus = () => {
    this.selectedAgentId = null;
    this.setActiveTab("costs");
  };

  /**
   * Handle user message sent event - detect task type, move agent, and analyze complexity
   */
  private handleUserMessageSent(e: CustomEvent<{ agentId: string; message: string }>) {
    const { agentId, message } = e.detail;
    if (!agentId || !message) return;

    // Detect task type from message content and move agent to appropriate desk
    if (this.pixelOffice) {
      const taskType = detectTaskType(message);
      if (taskType) {
        this.pixelOffice.moveAgentToTaskDesk(agentId, taskType);
      }
    }

    // Auto-delegate based on task analysis (no suggestions, just do it)
    const delegation = createDelegationPlan(message);

    // Show delegation indicator for multi-agent work
    if (delegation.supporting.length > 0 || delegation.ephemeralWorkers > 0) {
      this.activeDelegation = delegation;

      // Wake up relevant band members and trigger visual coordination
      if (this.pixelOffice) {
        // Wake primary band member
        this.pixelOffice.wakeBandMember(delegation.primary.id);

        // Wake supporting band members
        for (const supporter of delegation.supporting) {
          this.pixelOffice.wakeBandMember(supporter.id);
        }

        // If parallel work, show meeting animation
        if (delegation.parallel) {
          const meetingIds = [delegation.primary.id, ...delegation.supporting.map((s) => s.id)];
          this.pixelOffice.triggerMeeting(meetingIds);
        }
      }

      // Log delegation for debugging
      console.log("[RaxApp] Auto-delegating:", {
        primary: delegation.primary.name,
        supporting: delegation.supporting.map((s) => s.name),
        parallel: delegation.parallel,
        ephemeralWorkers: delegation.ephemeralWorkers,
      });

      // Clear indicator and put band members back to sleep after task completes
      // (approximated by timeout for now - in future, this would be event-driven)
      setTimeout(() => {
        if (this.activeDelegation === delegation) {
          this.activeDelegation = null;
          // Put band members back to sleep
          if (this.pixelOffice) {
            this.pixelOffice.sleepBandMember(delegation.primary.id);
            for (const supporter of delegation.supporting) {
              this.pixelOffice.sleepBandMember(supporter.id);
            }
          }
        }
      }, 30000); // Clear after 30s
    }
  }

  private handleOpenSettings(e: CustomEvent) {
    console.log("[RaxApp] Open settings:", e.detail.agentId);
    // For now, just show a placeholder - could open a modal later
    alert("Agent settings coming soon!");
  }

  /**
   * Handle band member woke event - a sleeping band member has been awakened for work
   */
  private handleBandMemberWoke(e: CustomEvent<{ bandMemberId: string }>) {
    console.log("[RaxApp] Band member woke:", e.detail.bandMemberId);
  }

  /**
   * Handle band delegation event - work delegated from one agent to a band member
   */
  private handleBandDelegation(e: CustomEvent<{ fromAgentId: string; toBandMemberId: string }>) {
    console.log("[RaxApp] Band delegation:", e.detail);
  }

  /**
   * Handle persona spawn event from gateway - a subagent with personaId was spawned.
   * Triggers pixel office animations for the corresponding band member.
   */
  private handlePersonaSpawned(
    e: CustomEvent<{ personaId: string; runId: string; sessionKey?: string; task?: string; label?: string }>,
  ) {
    const { personaId, runId, task, label } = e.detail;
    console.log("[RaxApp] Persona spawned:", personaId, { task, label });

    if (!this.pixelOffice) return;

    // Wake up the band member corresponding to this persona
    this.pixelOffice.wakeBandMember(personaId);

    // Track active personas (spawned but not yet completed)
    this._activePersonas.set(personaId, { runId, spawnedAt: Date.now() });

    // Track recently spawned personas for meeting trigger
    if (!this._recentSpawns) {
      this._recentSpawns = new Set<string>();
    }
    this._recentSpawns.add(personaId);

    // If multiple personas spawned within a short window, trigger a meeting
    if (this._spawnMeetingTimer) {
      clearTimeout(this._spawnMeetingTimer);
    }
    this._spawnMeetingTimer = window.setTimeout(() => {
      if (this._recentSpawns && this._recentSpawns.size >= 2) {
        // Trigger meeting with all recently spawned personas
        const meetingIds = Array.from(this._recentSpawns);
        console.log("[RaxApp] Triggering meeting with personas:", meetingIds);
        this.pixelOffice?.triggerMeeting(meetingIds, true);
      }
      this._recentSpawns = null;
      this._spawnMeetingTimer = null;
    }, 500); // 500ms window to collect concurrent spawns
  }

  // Track personas that are currently active (spawned but not completed)
  private _activePersonas: Map<string, { runId: string; spawnedAt: number }> = new Map();
  // Track recently spawned personas for meeting trigger window
  private _recentSpawns: Set<string> | null = null;
  private _spawnMeetingTimer: number | null = null;

  /**
   * Handle persona completion event from gateway - a subagent with personaId finished.
   * Triggers pixel office animations to show completion.
   */
  private handlePersonaCompleted(
    e: CustomEvent<{
      personaId: string;
      runId: string;
      sessionKey?: string;
      success: boolean;
      task?: string;
      label?: string;
    }>,
  ) {
    const { personaId, success, task, label } = e.detail;
    console.log("[RaxApp] Persona completed:", personaId, { success, task, label });

    // Remove from active tracking
    this._activePersonas.delete(personaId);

    if (!this.pixelOffice) return;

    if (success) {
      // Put the band member back to sleep (task completed successfully)
      this.pixelOffice.sleepBandMember(personaId);
    } else {
      // Show error state on the band member (task failed)
      this.pixelOffice.errorBandMember(personaId);
    }

    // If no more active personas, end any active meeting
    if (this._activePersonas.size === 0) {
      this.pixelOffice.endMeeting();
    }
  }

  private async handlePauseAgent(e: CustomEvent) {
    const { agentId } = e.detail;
    try {
      const result = await raxApi.pauseAgent(agentId);
      if (result.success && result.data) {
        this.agents = this.agents.map((a) =>
          a.id === agentId ? { ...a, status: "paused" as const } : a,
        );
      }
    } catch (err) {
      console.error("[RaxApp] Pause agent error:", err);
    }
  }

  private async handleResumeAgent(e: CustomEvent) {
    const { agentId } = e.detail;
    try {
      const result = await raxApi.resumeAgent(agentId);
      if (result.success && result.data) {
        this.agents = this.agents.map((a) =>
          a.id === agentId ? { ...a, status: "active" as const } : a,
        );
      }
    } catch (err) {
      console.error("[RaxApp] Resume agent error:", err);
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rax-app": RaxApp;
  }
}
